import React, { useState, useEffect } from "react";
import { Modal, Button, Text, BlockStack, Card, InlineStack, Icon, ButtonGroup, TextField } from "@shopify/polaris";
import { useNps } from "./useNpsHook";
import { InfoIcon, XIcon } from "@shopify/polaris-icons";

const NpsBanner: React.FC = () => {
    const {
        npsStatus,
        isSubmitting,
        isDismissing,
        checkNpsStatus,
        saveRating,
        saveFeedback,
        dismiss,
        openShopifyReviewModal,
    } = useNps();

    const [selectedRating, setSelectedRating] = useState<number | null>(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedback, setFeedback] = useState("");

    useEffect(() => {
        checkNpsStatus();
    }, [checkNpsStatus]);

    const handleRatingClick = async (rating: number) => {
        setSelectedRating(rating);

        const result = await saveRating(rating);

        if (result.success) {
            if (rating === 5) {
                // Open Shopify's native modal
                await openShopifyReviewModal();
            } else {
                // Show feedback modal for ratings 1-4
                setShowFeedbackModal(true);
            }
        }
    };

    const handleFeedbackSubmit = async () => {
        if (!feedback.trim()) return;

        const result = await saveFeedback(feedback);
        if (result.success) {
            setShowFeedbackModal(false);
        }
    };

    const handleFeedbackCancel = async () => {
        await dismiss();
        setShowFeedbackModal(false);
    };

    const handleDismiss = async () => {
        await dismiss();
    };

    if (!npsStatus) {
        return <></>;
    }

    return (
        <>
            {/* NPS Banner */}
            <Card padding="400">
                <BlockStack gap="300">
                    {/* Header */}
                    <InlineStack align="space-between" gap="200">
                        <InlineStack gap="200" align="center">
                            <Icon source={InfoIcon} tone="base" />
                            <Text as="h2" variant="headingMd">
                                How would you rate our app?
                            </Text>
                        </InlineStack>

                        <Button
                            variant="plain"
                            accessibilityLabel="Dismiss"
                            onClick={handleDismiss}
                            loading={isDismissing}
                            disabled={isDismissing || isSubmitting}
                            icon={XIcon}
                        />
                    </InlineStack>

                    {/* Rating Row (single line) */}
                    <InlineStack align="space-between" gap="400" blockAlign="center">
                        <Text as="p" variant="bodyMd" tone="subdued">
                            Your rating helps us improve
                        </Text>

                        <ButtonGroup>
                            {[1, 2, 3, 4, 5].map((rating: any) => (
                                <Button
                                    key={rating}
                                    pressed={selectedRating === rating}
                                    onClick={() => handleRatingClick(rating)}
                                    disabled={isSubmitting}
                                >
                                    {rating}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </InlineStack>
                </BlockStack>
            </Card>

            {/* Feedback Modal for ratings 1–4 */}
            <Modal
                open={showFeedbackModal}
                onClose={handleFeedbackCancel}
                title="Help us improve"
                primaryAction={{
                    content: "Submit",
                    onAction: handleFeedbackSubmit,
                    loading: isSubmitting,
                    disabled: isSubmitting || feedback.trim().length === 0 || isDismissing,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: handleFeedbackCancel,
                        loading: isDismissing,
                        disabled: isSubmitting || isDismissing,
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                            Tell us what we can do better
                        </Text>

                        <TextField
                            label="Feedback"
                            labelHidden
                            multiline={5}
                            placeholder="Leave your comments here..."
                            value={feedback}
                            onChange={setFeedback}
                            disabled={isSubmitting}
                            autoComplete="off"
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </>
    );
};

export default NpsBanner;
